import { isEmpty } from 'lodash';
import { HYPERVISOR_TYPE, VCENTER_TYPE } from '../../../filters/virtual-infrastructure-type-filter/SubTypeHelper';
import { ID_MAPPING, SUBTYPE_FIELD } from '../InfrastructureConstants';

/**
 * Constant defining which virtual infrastructure types require user confirmation
 * before certain actions (e.g., disable/enable).
 */
const TYPES_REQUIRING_CONFIRMATION = new Set([VCENTER_TYPE, HYPERVISOR_TYPE]);

/**
 * Extracts the set of sub-types from the current table selection.
 * Handles both normal selection and inverted selection (select all except).
 *
 * @param {Object} selectionData - The selection data from the data table
 * @param {Object} selectionData.currentTableSelection - Current selection state
 * @param {Object} selectionData.totalRows - All rows in the table
 * @returns {Set|null} Set of selected sub-types, or null if no selection
 */
const extractSelectedSubTypes = (selectionData) => {
    if (isEmpty(selectionData)) {
        return null;
    }

    const { currentTableSelection, totalRows } = selectionData;
    const rowsArray = totalRows ? Object.values(totalRows) : [];

    if (currentTableSelection.inverted) {
        return new Set(rowsArray.map((rowObj) => rowObj.row[SUBTYPE_FIELD]));
    }

    const selectionArr = currentTableSelection?.selection;
    if (isEmpty(selectionArr)) {
        return null;
    }

    return selectionArr.reduce((selectedSubtypes, selectedId) => {
        const matchRow = rowsArray.find((rowObj) => rowObj?.row?.[ID_MAPPING] === selectedId);
        if (matchRow) {
            selectedSubtypes.add(matchRow.row[SUBTYPE_FIELD]);
        }
        return selectedSubtypes;
    }, new Set());
};

/**
 * Determines if any selected types require user confirmation.
 * vCenter and Hypervisor types require confirmation for certain actions.
 *
 * @param {Set|null} subTypeSet - Set of selected sub-types
 * @returns {boolean} True if any type requires confirmation
 */
const hasTypeRequiringConfirmation = (subTypeSet) => {
    if (!subTypeSet) {
        return false;
    }
    return Array.from(subTypeSet).some((type) => TYPES_REQUIRING_CONFIRMATION.has(type));
};

/**
 * Custom hook to initialize virtual infrastructure list data and utilities.
 * Provides helper functions for selection type extraction and confirmation logic.
 *
 * This hook encapsulates the business logic for:
 * - Determining which infrastructure types require confirmation
 * - Extracting sub-types from table selections
 * - Checking if confirmation dialogs should be shown
 *
 * @returns {Object} Initialization utilities
 * @returns {Function} extractSelectedSubTypes - Extract sub-types from selection data
 * @returns {Function} hasTypeRequiringConfirmation - Check if confirmation needed
 * @returns {Set} TYPES_REQUIRING_CONFIRMATION - Constant set of types requiring confirmation
 *
 * @example
 * function VirtualInfrastructureList() {
 *   const { extractSelectedSubTypes, hasTypeRequiringConfirmation } = useVirtualInfrastructureInit();
 *   // Use functions to determine if confirmation dialog should be shown
 * }
 */
export function useVirtualInfrastructureInit() {
    return {
        extractSelectedSubTypes,
        hasTypeRequiringConfirmation,
        TYPES_REQUIRING_CONFIRMATION,
    };
}
